import { useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import { ResourceLinkThumb } from "@/components/agent/details/ResourceThumb";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useKnowledgeLinks } from "@/hooks/agent/useKnowledgeLinks";
import { linkUrlKey } from "@/lib/agent/bulkInfoLinks";
import type { KnowledgeLink } from "@/services/knowledgebase/KnowledgeLinksService";
import type { InfoLink } from "@/types/agent/info-links";

interface KnowledgeLinkSuggestionsProps {
  agentId: string;
  brand: string;
  existingLinks: InfoLink[];
  /** URL keys of suggestions the owner chose not to see again. */
  dismissedKeys: string[];
  /** How many more links the Resources list can take. */
  slots: number;
  onAdd: (links: InfoLink[]) => void;
  onDismiss: (urlKeys: string[]) => void;
}

const toInfoLink = (link: KnowledgeLink, index: number): InfoLink => ({
  id: `link-${Date.now()}-${index}`,
  label: link.label,
  url: link.url,
  description: link.description,
  ...(link.description ? { descriptionSource: "ai" as const } : {}),
});

/**
 * Offers the links and pictures found inside the genie's knowledge documents.
 * Nothing reaches the Resources list until the owner picks it, and a
 * suggestion they dismiss stays out of later scans.
 */
export const KnowledgeLinkSuggestions = ({
  agentId,
  brand,
  existingLinks,
  dismissedKeys,
  slots,
  onAdd,
  onDismiss,
}: KnowledgeLinkSuggestionsProps) => {
  const { links, documents, isLoading, scan, startScan, isStarting } = useKnowledgeLinks(agentId);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const available = useMemo(() => {
    const hidden = new Set([...existingLinks.map((link) => linkUrlKey(link.url)), ...dismissedKeys]);
    return links.filter((link) => !hidden.has(linkUrlKey(link.url)));
  }, [links, existingLinks, dismissedKeys]);

  const groups = useMemo(() => {
    const byDocument = new Map<string, { name: string; links: KnowledgeLink[] }>();
    for (const link of available) {
      const group = byDocument.get(link.documentId) ?? { name: link.documentName, links: [] };
      group.links.push(link);
      byDocument.set(link.documentId, group);
    }
    return [...byDocument.entries()];
  }, [available]);

  if (isLoading || !documents || documents.total === 0) return null;

  const chosen = available.filter((link) => selected.has(link.url));
  const isScanning = !!scan;

  const toggle = (url: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(url)) next.add(url);
      return next;
    });

  const handleAdd = (picked: KnowledgeLink[]) => {
    if (slots <= 0) {
      toast.error("The Resources list is full — remove a link to add another.");
      return;
    }
    const accepted = picked.slice(0, slots);
    onAdd(accepted.map(toInfoLink));
    setSelected(new Set());
    toast.success(
      accepted.length < picked.length
        ? `${accepted.length} added — the list is full, so the rest were left out. Remember to save.`
        : `${accepted.length} link${accepted.length === 1 ? "" : "s"} added — remember to save`,
    );
  };

  const status = isScanning
    ? `Reading ${Math.min(scan.scanned + 1, scan.total)} of ${scan.total} document${scan.total === 1 ? "" : "s"}…`
    : available.length > 0
      ? "Links and pictures mentioned in what your Genie already knows. Pick the ones visitors should see."
      : documents.pending > 0
        ? `${documents.pending} document${documents.pending === 1 ? " hasn't" : "s haven't"} been read for links yet.`
        : "Nothing new to suggest — every document has been read.";

  return (
    <div className="rounded-lg border border-dashed border-genie-primary/40 bg-genie-primary-light/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <Search className="w-4 h-4 text-genie-primary" />
            Links from your knowledge base
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{status}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {chosen.length > 0 && (
            <Button
              type="button"
              size="sm"
              onClick={() => handleAdd(chosen)}
              className="min-h-[44px] sm:min-h-0 gap-1 bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-md shadow-genie-primary/30"
            >
              <Plus className="w-3.5 h-3.5" />
              Add selected ({chosen.length})
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => startScan(documents.pending === 0)}
            disabled={isScanning || isStarting}
            className="min-h-[44px] sm:min-h-0 gap-1 bg-white"
          >
            {isScanning || isStarting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : documents.pending > 0 ? (
              <Search className="w-3.5 h-3.5" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            {isScanning ? "Reading…" : documents.pending > 0 ? "Find links" : "Check again"}
          </Button>
        </div>
      </div>

      {isScanning && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
          <div
            className="h-full rounded-full bg-gradient-to-r from-genie-primary to-genie-secondary transition-all duration-500"
            style={{ width: `${Math.max(5, Math.round((scan.scanned / Math.max(scan.total, 1)) * 100))}%` }}
          />
        </div>
      )}

      {groups.length > 0 && (
        <div className="mt-3 space-y-3">
          {groups.map(([documentId, group]) => (
            <div key={documentId}>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <p className="min-w-0 truncate text-xs font-semibold text-gray-600">From “{group.name}”</p>
                {group.links.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setSelected((prev) => new Set([...prev, ...group.links.map((link) => link.url)]))}
                    className="flex-shrink-0 text-xs text-genie-primary hover:underline"
                  >
                    Select all
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {group.links.map((link) => (
                  <div key={link.url} className="flex items-start gap-2 rounded-md border border-gray-200 bg-white p-2">
                    <Checkbox
                      checked={selected.has(link.url)}
                      onCheckedChange={() => toggle(link.url)}
                      aria-label={`Select ${link.label}`}
                      className="mt-1"
                    />
                    <ResourceLinkThumb link={{ id: link.url, label: link.label, url: link.url }} brand={brand} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{link.label}</div>
                      <div className="text-xs text-gray-500 truncate">{link.url}</div>
                      {link.description && (
                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">{link.description}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onDismiss([linkUrlKey(link.url)])}
                      className="flex-shrink-0 p-2.5 sm:p-1 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label={`Don't suggest ${link.label} again`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
